'use client';
import React, { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Percent,
  Star,
  Hash,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { supabase, getHondurasDateStr } from '@/lib/supabase/bunori';
import KpiCard from './KpiCard';
import QuickActions from './QuickActions';

const SalesChart = dynamic(() => import('./SalesChart'), { ssr: false });
const TopProductsChart = dynamic(() => import('./TopProductsChart'), { ssr: false });

interface DashboardStats {
  totalSales: number;
  totalProfit: number;
  totalCosts: number;
  totalCommissions: number;
  numSales: number;
  topProduct: { name: string; count: number; revenue: number } | null;
  lowStockIngredients: { id: string; name: string; stock: number; min_stock: number; unit: string; status: string }[];
  todaySales: { id: string; receipt_number: string; sale_time: string; payment_method: string; total: number; profit: number }[];
}

const emptyStats: DashboardStats = {
  totalSales: 0,
  totalProfit: 0,
  totalCosts: 0,
  totalCommissions: 0,
  numSales: 0,
  topProduct: null,
  lowStockIngredients: [],
  todaySales: [],
};

export default function DashboardContent() {
  const [stats, setStats] = useState<DashboardStats>(emptyStats);
  const [hourlyData, setHourlyData] = useState<{ hour: string; ventas: number; ganancia: number }[]>([]);
  const [topProducts, setTopProducts] = useState<{ name: string; count: number }[]>([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      // Use Honduras local date — never UTC — so "today" matches the business calendar
      const today = getHondurasDateStr();

      const [salesRes, ingredientsRes] = await Promise.all([
        supabase.from('sales').select('*, items:sale_items(*)').eq('sale_date', today).eq('status', 'completed'),
        supabase.from('ingredients').select('id, name, stock, min_stock, unit, status').in('status', ['low', 'critical', 'out']),
      ]);

      const todaySales = salesRes.data || [];
      const lowStock = ingredientsRes.data || [];

      const totalSales = todaySales.reduce((s: number, sale: { total: number }) => s + Number(sale.total), 0);
      const totalProfit = todaySales.reduce((s: number, sale: { profit: number }) => s + Number(sale.profit), 0);
      const totalCommissions = todaySales.reduce((s: number, sale: { pedidos_ya_commission: number }) => s + Number(sale.pedidos_ya_commission), 0);
      const totalCosts = totalSales - totalProfit;

      // Top products
      const productCount: Record<string, { name: string; count: number; revenue: number }> = {};
      todaySales.forEach((sale: { items?: { product_name: string; product_id: string; quantity: number; subtotal: number }[] }) => {
        (sale.items || []).forEach((item) => {
          if (!productCount[item.product_id]) {
            productCount[item.product_id] = { name: item.product_name, count: 0, revenue: 0 };
          }
          productCount[item.product_id].count += item.quantity;
          productCount[item.product_id].revenue += Number(item.subtotal);
        });
      });
      const topProduct = Object.values(productCount).sort((a, b) => b.count - a.count)[0] || null;
      const topProductsList = Object.values(productCount).sort((a, b) => b.count - a.count).slice(0, 5);

      // Hourly data
      const hours = Array.from({ length: 12 }, (_, i) => {
        const hour = i + 7;
        const label = `${hour}:00`;
        const salesInHour = todaySales.filter((s: { sale_time: string }) => {
          const h = parseInt(s.sale_time.split(':')[0]);
          return h === hour;
        });
        return {
          hour: label,
          ventas: salesInHour.reduce((s: number, sale: { total: number }) => s + Number(sale.total), 0),
          ganancia: salesInHour.reduce((s: number, sale: { profit: number }) => s + Number(sale.profit), 0),
        };
      });

      setStats({
        totalSales,
        totalProfit,
        totalCosts,
        totalCommissions,
        numSales: todaySales.length,
        topProduct,
        lowStockIngredients: lowStock,
        todaySales: todaySales.map((s: { id: string; receipt_number: string; sale_time: string; payment_method: string; total: number; profit: number }) => ({
          id: s.id,
          receipt_number: s.receipt_number,
          sale_time: s.sale_time,
          payment_method: s.payment_method,
          total: Number(s.total),
          profit: Number(s.profit),
        })),
      });
      setHourlyData(hours);
      setTopProducts(topProductsList);

      const now = new Date();
      setLastUpdated(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    } catch {
      // silently fail — dashboard shows zeros
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const fmt = (n: number) => `L ${n.toFixed(2)}`;

  return (
    <div className="p-6 xl:p-8 2xl:p-10 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Resumen del día de hoy
          </p>
        </div>
        <button onClick={loadStats} disabled={loading} className="btn-secondary gap-2 text-sm disabled:opacity-60">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {lastUpdated && <span className="text-xs text-muted-foreground">Actualizado {lastUpdated}</span>}
        </button>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="section-title mb-3">Accesos rápidos</h2>
        <QuickActions />
      </div>

      {/* KPI Bento Grid */}
      <div className="mb-8">
        <h2 className="section-title mb-4">Resumen del día</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
          <KpiCard
            label="Ventas del día"
            value={fmt(stats.totalSales)}
            subValue={`${stats.numSales} transacciones`}
            icon={<TrendingUp size={20} />}
            accentColor="purple"
            className="sm:col-span-2"
          />
          <KpiCard
            label="Ganancia del día"
            value={fmt(stats.totalProfit)}
            subValue={`Margen ${stats.totalSales > 0 ? ((stats.totalProfit / stats.totalSales) * 100).toFixed(1) : 0}%`}
            icon={<DollarSign size={20} />}
            accentColor="green"
          />
          <KpiCard
            label="Costos del día"
            value={fmt(stats.totalCosts)}
            subValue="Costo de ingredientes"
            icon={<ShoppingCart size={20} />}
            accentColor="amber"
          />
          <KpiCard
            label="Comisiones PedidosYa"
            value={fmt(stats.totalCommissions)}
            subValue="Comisiones del día"
            icon={<Percent size={20} />}
            accentColor="pink"
          />
          <KpiCard
            label="Número de ventas"
            value={String(stats.numSales)}
            subValue="Transacciones hoy"
            icon={<Hash size={20} />}
            accentColor="cyan"
          />
          <KpiCard
            label="Producto más vendido"
            value={stats.topProduct?.name || '—'}
            subValue={stats.topProduct ? `${stats.topProduct.count} unidades` : 'Sin ventas hoy'}
            icon={<Star size={20} />}
            accentColor="purple"
          />
          <KpiCard
            label="Alertas de inventario"
            value={String(stats.lowStockIngredients.length)}
            subValue="Ingredientes con stock bajo"
            icon={<AlertTriangle size={20} />}
            accentColor={stats.lowStockIngredients.length > 0 ? 'red' : 'green'}
          />
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6 mb-8">
        <div className="card-base p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="section-title">Ventas por hora</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Hoy</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-1.5 rounded-full bg-primary inline-block" />
                Ventas
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-1.5 rounded-full bg-accent inline-block" />
                Ganancia
              </span>
            </div>
          </div>
          <SalesChart data={hourlyData} />
        </div>

        <div className="card-base p-5">
          <div className="mb-4">
            <h2 className="section-title">Top productos</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Unidades vendidas hoy</p>
          </div>
          {topProducts.length > 0 ? (
            <TopProductsChart data={topProducts} />
          ) : (
            <div className="flex flex-col items-center justify-center h-[180px] text-center">
              <ShoppingCart size={32} className="text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Sin ventas registradas hoy</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom row: recent sales + low stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
        <div className="card-base p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title">Ventas recientes</h2>
            <a href="/recibos" className="text-xs text-primary font-600 hover:underline">Ver todas →</a>
          </div>
          <div className="space-y-0">
            {stats.todaySales.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <ShoppingCart size={28} className="text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Sin ventas hoy</p>
              </div>
            ) : stats.todaySales.slice(0, 5).map((sale) => (
              <div key={`recent-${sale.id}`} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center text-primary">
                    <ShoppingCart size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-600 text-foreground">{sale.receipt_number}</p>
                    <p className="text-xs text-muted-foreground">{sale.sale_time} · {sale.payment_method}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-700 font-tabular text-foreground">L {sale.total.toFixed(2)}</p>
                  <p className="text-xs text-[var(--status-green)] font-500">+L {sale.profit.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-base p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title">Alertas de inventario</h2>
            <a href="/ingredientes" className="text-xs text-primary font-600 hover:underline">Ver inventario →</a>
          </div>
          {stats.lowStockIngredients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[var(--status-green-bg)] flex items-center justify-center mb-3">
                <AlertTriangle size={22} className="text-[var(--status-green)]" />
              </div>
              <p className="text-sm font-600 text-foreground">Todo en orden</p>
              <p className="text-xs text-muted-foreground mt-1">Todos los ingredientes tienen stock suficiente</p>
            </div>
          ) : (
            <div className="space-y-0">
              {stats.lowStockIngredients.map((ing) => (
                <div key={`alert-${ing.id}`} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                  <div>
                    <p className="text-sm font-600 text-foreground">{ing.name}</p>
                    <p className="text-xs text-muted-foreground">Stock: {ing.stock} {ing.unit} · Mín: {ing.min_stock} {ing.unit}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-lg text-xs font-600 ${
                    ing.status === 'out' ? 'bg-gray-200 text-gray-600' :
                    ing.status === 'critical'? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {ing.status === 'out' ? 'Agotado' : ing.status === 'critical' ? 'Crítico' : 'Stock bajo'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}